import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static targets = ["status", "erros", "planos", "evolucao"]
  static values = { data: Object, refreshInterval: { type: Number, default: 0 } }

  connect() {
    this.charts = []
    this.styles = getComputedStyle(document.documentElement)
    this.primary = this.cssColor("--bs-primary", "#0d6efd")
    this.text = this.cssColor("--bs-body-color", "#495057")
    this.muted = this.cssColor("--bs-secondary-color", "#6c757d")
    this.border = this.cssColor("--bs-border-color", "#dee2e6")
    this.darkMode = document.documentElement.dataset.bsTheme === "dark" ||
      document.body.classList.contains("mod-skin-dark")
    this.renderStatus()
    this.renderErros()
    this.renderPlanos()
    this.renderEvolucao()
    if (this.refreshIntervalValue > 0) {
      this.refreshTimer = window.setInterval(() => this.refresh(), this.refreshIntervalValue)
    }
  }

  disconnect() {
    if (this.refreshTimer) window.clearInterval(this.refreshTimer)
    this.charts.forEach(chart => chart.destroy())
  }

  refresh() {
    if (document.visibilityState !== "visible") return
    if (this.element.querySelector(":focus")) return

    if (window.Turbo) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }

  renderStatus() {
    const data = this.dataValue.status
    this.render(this.statusTarget, {
      chart: { type: "bar", height: 300, toolbar: { show: false } },
      series: [{ name: "Cobranças", data: data.values }],
      colors: [this.primary],
      plotOptions: { bar: { horizontal: true, borderRadius: 5, distributed: false } },
      dataLabels: { enabled: true },
      xaxis: { categories: data.labels, labels: { style: { colors: this.muted } } },
      yaxis: { labels: { style: { colors: this.text }, maxWidth: 180 } },
      grid: { borderColor: this.border },
      tooltip: { theme: this.darkMode ? "dark" : "light" }
    })
  }

  renderErros() {
    const data = this.dataValue.erros
    this.render(this.errosTarget, {
      chart: { type: "bar", height: 300, toolbar: { show: false } },
      series: [{ name: "Ocorrências", data: data.values }],
      colors: [this.cssColor("--bs-danger", "#dc3545")],
      plotOptions: { bar: { borderRadius: 5, columnWidth: "48%" } },
      dataLabels: { enabled: true },
      xaxis: { categories: data.labels, labels: { rotate: -20, style: { colors: this.muted } } },
      yaxis: { labels: { style: { colors: this.muted } } },
      grid: { borderColor: this.border },
      tooltip: { theme: this.darkMode ? "dark" : "light" }
    })
  }

  renderPlanos() {
    const data = this.dataValue.planos
    this.render(this.planosTarget, {
      chart: { type: "bar", height: 300, toolbar: { show: false } },
      series: [{ name: "Valor", data: data.values }],
      colors: [this.primary],
      plotOptions: { bar: { horizontal: true, borderRadius: 5 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.labels,
        labels: { style: { colors: this.muted }, formatter: value => this.compactMoney(value) }
      },
      yaxis: { labels: { style: { colors: this.text }, maxWidth: 150 } },
      grid: { borderColor: this.border },
      tooltip: { theme: this.darkMode ? "dark" : "light", y: { formatter: value => this.money(value) } }
    })
  }

  renderEvolucao() {
    const data = this.dataValue.evolucao
    this.render(this.evolucaoTarget, {
      chart: { type: "area", height: 320, toolbar: { show: false } },
      series: [{ name: "Valor previsto", data: data.values }],
      colors: [this.primary],
      stroke: { curve: "smooth", width: 3 },
      fill: {
        type: "gradient",
        gradient: { shadeIntensity: 1, opacityFrom: 0.42, opacityTo: 0.04, stops: [0, 95] }
      },
      markers: { size: 4, strokeWidth: 0 },
      dataLabels: { enabled: false },
      xaxis: { categories: data.labels, labels: { style: { colors: this.muted } } },
      yaxis: {
        labels: { style: { colors: this.muted }, formatter: value => this.compactMoney(value) }
      },
      grid: { borderColor: this.border, strokeDashArray: 4 },
      tooltip: { theme: this.darkMode ? "dark" : "light", y: { formatter: value => this.money(value) } }
    })
  }

  render(element, options) {
    if (!element || !options.series?.some(series => series.data.length > 0)) {
      element.innerHTML = '<div class="rec-empty-chart">Sem dados para o período</div>'
      return
    }

    const chart = new ApexCharts(element, {
      ...options,
      chart: { ...options.chart, background: "transparent", foreColor: this.text },
      theme: { mode: this.darkMode ? "dark" : "light" }
    })
    this.charts.push(chart)
    chart.render()
  }

  money(value) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0)
  }

  compactMoney(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      notation: "compact",
      maximumFractionDigits: 1
    }).format(value || 0)
  }

  cssColor(variable, fallback) {
    return this.styles.getPropertyValue(variable).trim() || fallback
  }
}
